'use client';

import { useTranslations } from 'next-intl';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Typography } from '@/components/ui/typography';
import { MousePointerClick, Users, ArrowRight } from 'lucide-react';

interface Plan {
  name: string;
}

interface HowItWorksSectionProps {
  plan: Plan;
}

export function HowItWorksSection({ plan }: HowItWorksSectionProps) {
  const t = useTranslations('planDetails.clickPlan.howItWorks');

  const paragraphs = [
    {
      icon: MousePointerClick,
      color: 'primary',
      text: t('paragraph1'),
    },
    {
      icon: Users,
      color: 'blue',
      text: t('paragraph2'),
    },
  ];

  const getColorClasses = (color: string) => {
    const colors = {
      primary: {
        bg: 'bg-primary-50 dark:bg-primary-900/20',
        icon: 'text-primary-600 dark:text-primary-400',
        border: 'border-primary-200 dark:border-primary-800',
        hover: 'hover:border-primary-400 dark:hover:border-primary-600',
        iconBg: 'bg-primary-500 dark:bg-primary-600',
      },
      blue: {
        bg: 'bg-blue-50 dark:bg-blue-900/20',
        icon: 'text-blue-600 dark:text-blue-400',
        border: 'border-blue-200 dark:border-blue-800',
        hover: 'hover:border-blue-400 dark:hover:border-blue-600',
        iconBg: 'bg-blue-500 dark:bg-blue-600',
      },
    };
    return colors[color as keyof typeof colors] || colors.primary;
  };

  return (
    <SectionWrapper variant="gradient" padding="lg" className="relative">
      {/* Decorative background elements */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute left-1/4 top-0 h-96 w-96 rounded-full bg-primary-500/5 blur-3xl" />
        <div className="absolute right-1/4 bottom-0 h-96 w-96 rounded-full bg-blue-500/5 blur-3xl" />
      </div>

      <div className="container relative z-10">
        <div className="text-center mb-16">
          <Typography variant="h2" className="mb-4 text-gray-900 dark:text-white">
            {t('title')}
          </Typography>
          {t('description') && (
            <Typography variant="p" className="text-lg text-gray-600 dark:text-gray-400 max-w-4xl mx-auto">
              {t('description')}
            </Typography>
          )}
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {paragraphs.map((para, index) => {
              const Icon = para.icon;
              const colors = getColorClasses(para.color);
              const isLast = index === paragraphs.length - 1;

              return (
                <div key={index} className="relative group">
                  {/* Floating Icon Badge */}
                  <div className={`absolute -top-4 -left-4 w-16 h-16 rounded-2xl ${colors.iconBg} flex items-center justify-center shadow-xl z-10 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>

                  {/* Main Card */}
                  <div className={`relative bg-white dark:bg-gray-800 rounded-2xl p-8 pt-12 border-2 ${colors.border} ${colors.hover} transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 overflow-hidden`}>
                    {/* Decorative corner accent */}
                    <div className={`absolute top-0 right-0 w-32 h-32 ${colors.bg} rounded-bl-full opacity-50`}></div>

                    {/* Content */}
                    <div className="relative z-10">
                      <Typography variant="p" className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg">
                        {para.text}
                      </Typography>
                    </div>

                    {/* Bottom accent line */}
                    <div className={`absolute bottom-0 left-0 right-0 h-1 ${colors.iconBg} opacity-30`}></div>
                  </div>

                  {/* Arrow connector between cards */}
                  {!isLast && (
                    <div className="hidden md:block absolute top-1/2 -right-6 transform -translate-y-1/2 z-10">
                      <div className="w-12 h-12 rounded-full bg-white dark:bg-gray-800 border-2 border-primary-200 dark:border-primary-800 flex items-center justify-center shadow-lg">
                        <ArrowRight className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
}

